import { useEffect, useState } from "react";
import { Package } from "lucide-react";
import { WHATSAPP_URL, LOGO, TAGLINE } from "@/lib/sections";

// Authentic WhatsApp brand glyph in official brand green (#25D366).
const WhatsAppIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="#25D366" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
  </svg>
);

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  // pastCover = user has left the cover slide → reveal the small nav logo
  const [pastCover, setPastCover] = useState(false);

  useEffect(() => {
    const container = document.getElementById("deck");
    if (!container) return;
    const onScroll = () => {
      const top = container.scrollTop;
      setScrolled(top > 80);
      // Reveal the small nav-logo after ~30% of the first viewport — feels like a smooth handoff from the cover logo
      setPastCover(top > container.clientHeight * 0.3);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
    // container/onScroll are created inside the effect; setScrolled/setPastCover
    // are stable React setters — no stale-closure risk. Empty deps is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bridge to the bottom "Request Samples" CTA: scroll into Contact and open the modal
  const requestSamples = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    // Small delay so the modal opens after the scroll animation lands
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("aarya:request-samples"));
    }, 450);
  };

  return (
    <header
      data-testid="top-nav"
      className={`no-print fixed left-0 right-0 top-0 z-50 transition-all duration-700 ease-luxe ${
        scrolled
          ? "bg-ink/75 backdrop-blur-xl py-2.5 md:py-3"
          : "bg-transparent py-4 md:py-5"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 md:px-12 lg:px-16">
        {/* Official brand logo — hidden on the cover slide, fades in once user scrolls past */}
        <a
          href="#cover"
          data-testid="brand-logo"
          aria-label="Aarya24kt"
          className={`group flex items-center gap-4 transition-all duration-700 ease-luxe ${
            pastCover
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-1 pointer-events-none"
          }`}
        >
          <img
            src={LOGO.mark}
            alt="Aarya24kt"
            className={`w-auto object-contain transition-all duration-700 ease-luxe ${
              scrolled ? "h-11 md:h-12" : "h-12 md:h-14"
            }`}
            crossOrigin="anonymous"
          />
          <span
            data-testid="nav-tagline"
            className="hidden lg:inline font-heading italic text-gold/90 text-[13px] xl:text-sm tracking-wide whitespace-nowrap border-l border-gold/25 pl-4"
          >
            {TAGLINE}
          </span>
        </a>

        {/* CTAs */}
        <div className="flex items-center gap-2 md:gap-3 ml-auto">
          {/* Mobile: icon-only WhatsApp (44px tap target) in brand green */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-btn"
            aria-label="Chat on WhatsApp"
            className="group md:hidden inline-flex items-center justify-center h-11 w-11 border border-ivory/15 hover:border-[#25D366]/60 transition-colors duration-500 ease-luxe"
          >
            <WhatsAppIcon className="h-[20px] w-[20px]" />
          </a>
          {/* Desktop: labeled WhatsApp in brand green */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-btn-desktop"
            className="group hidden md:inline-flex items-center gap-2 border border-ivory/15 px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] text-ivory/80 hover:border-[#25D366]/60 hover:text-ivory transition-colors duration-500 ease-luxe"
          >
            <WhatsAppIcon className="h-4 w-4" />
            <span>WhatsApp</span>
          </a>
          {/* Primary CTA — mirrors the bottom "Request Samples" card */}
          <button
            onClick={requestSamples}
            data-testid="nav-request-samples-btn"
            aria-label="Request complimentary samples from Aarya24KT"
            className="group inline-flex items-center gap-2 bg-gold px-4 md:px-5 py-3 md:py-2.5 text-[10.5px] md:text-[11px] uppercase tracking-[0.22em] md:tracking-[0.25em] text-ink hover:bg-ivory transition-colors duration-500 ease-luxe"
          >
            <Package className="h-3.5 w-3.5" />
            <span>Request Samples</span>
          </button>
        </div>
      </div>
    </header>
  );
}
